import React, { useState } from 'react';
import {
 Award,
 CheckCircle,
 XCircle,
 Plus,
 Edit,
 Trash2,
 Star,
 MapPin,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect, PillToggle } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateVendorSpotlightModal from '../../components/admin/CreateVendorSpotlightModal';
import { useAdminVendorSpotlight } from '../../hooks/useAdminVendorSpotlight';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminVendorSpotlight = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [editingSpotlight, setEditingSpotlight] = useState(null);
 const { success, error: showError } = useToastContext();
 const {
 spotlights,
 isLoading,
 error,
 filters,
 activeStatusOptions,
 handleFilterChange,
 statistics,
 createSpotlight,
 isCreating,
 updateSpotlight,
 isUpdating,
 toggleStatus,
 togglingSpotlightId,
 deleteSpotlight,
 isDeleting
 } = useAdminVendorSpotlight();

 const handleToggleStatus = (spotlight) => {
 toggleStatus(spotlight.id, {
 onSuccess: () => {
 success(
 'Status Updated',
 `Vendor spotlight has been ${spotlight.is_active ? 'deactivated' : 'activated'} successfully.`
 );
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || 'Failed to update status.';
 showError('Status Update Failed', errorMessage);
 },
 });
 };

 const columns = [
 {
 key: 'name',
 label: 'Name',
 sortable: true,
 width: 'w-64',
 render: (spotlight) => (
 <div className="flex items-center gap-3">
 {spotlight.profile_image && (
 <img
 src={spotlight.profile_image}
 alt={spotlight.name}
 className="w-10 h-10 rounded-full object-cover"
 />
 )}
 <div>
 <p className="text-sm font-medium text-ink">
 {spotlight.name || 'N/A'}
 </p>
 {spotlight.badge && (
 <span className="text-xs text-ink-tertiary">{spotlight.badge}</span>
 )}
 </div>
 </div>
 )
 },
 {
 key: 'category',
 label: 'Category',
 sortable: true,
 width: 'w-48',
 render: (spotlight) => (
 <p className="text-sm text-ink">
 {spotlight.category || 'N/A'}
 </p>
 )
 },
 {
 key: 'location',
 label: 'Location',
 sortable: true,
 width: 'w-48',
 render: (spotlight) => (
 <div className="flex items-center gap-1 text-sm text-ink-secondary">
 <MapPin className="w-4 h-4" />
 {spotlight.location || 'N/A'}
 </div>
 )
 },
 {
 key: 'stats',
 label: 'Stats',
 sortable: false,
 width: 'w-48',
 render: (spotlight) => (
 <div className="text-xs text-ink-secondary space-y-1">
 {spotlight.rating && (
 <div className="flex items-center gap-1">
 <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
 <span className="font-medium">{spotlight.rating}</span>
 {spotlight.review_count && (
 <span>({spotlight.review_count})</span>
 )}
 </div>
 )}
 {spotlight.completed_events && (
 <div>{spotlight.completed_events} events</div>
 )}
 </div>
 )
 },
 {
 key: 'is_active',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (spotlight) => (
 <PillToggle
 checked={Boolean(spotlight.is_active)}
 onChange={() => handleToggleStatus(spotlight)}
 loading={togglingSpotlightId === spotlight.id}
 variant="success"
 onLabel="On"
 offLabel="Off"
 ariaLabel={`${spotlight.name || 'Vendor'} spotlight status`}
 />
 ),
 },
 ];

 const actions = [
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 onClick: (spotlight) => {
 setEditingSpotlight(spotlight);
 setShowCreateModal(true);
 },
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (spotlight) => {
 if (window.confirm('Are you sure you want to delete this vendor spotlight?')) {
 deleteSpotlight(spotlight.id, {
 onSuccess: () => {
 success('Vendor Spotlight Deleted', 'The vendor spotlight has been deleted successfully.');
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || 'Failed to delete vendor spotlight.';
 showError('Deletion Failed', errorMessage);
 }
 });
 }
 },
 disabled: isDeleting
 }
 ];

 const handleCreate = (data) => {
 createSpotlight(data, {
 onSuccess: () => {
 setShowCreateModal(false);
 setEditingSpotlight(null);
 success('Vendor Spotlight Created', 'The vendor spotlight has been created successfully.');
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || 'Failed to create vendor spotlight.';
 showError('Creation Failed', errorMessage);
 }
 });
 };

 const handleUpdate = (data) => {
 updateSpotlight(
 { id: editingSpotlight.id, data },
 {
 onSuccess: () => {
 setShowCreateModal(false);
 setEditingSpotlight(null);
 success('Vendor Spotlight Updated', 'The vendor spotlight has been updated successfully.');
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || 'Failed to update vendor spotlight.';
 showError('Update Failed', errorMessage);
 }
 }
 );
 };

 if (error) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50/80 p-8 text-center">
 <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
 <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Vendor Spotlights</h3>
 <p className="text-red-600 text-sm">
 {error?.response?.data?.message || 'Failed to load vendor spotlights. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Vendor Spotlight"
 subtitle="Manage featured vendor spotlights displayed on the homepage."
 actions={
 <Button
 className="rounded-md flex items-center gap-2"
 onClick={() => {
 setEditingSpotlight(null);
 setShowCreateModal(true);
 }}
 >
 <Plus className="w-4 h-4" />
 Create Spotlight
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendorSpotlight.title}
 steps={ADMIN_PAGE_GUIDES.vendorSpotlight.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Spotlights</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">
 {statistics.total_spotlights || 0}
 </p>
 </div>
 <Award className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Active</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">
 {statistics.active_spotlights || 0}
 </p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Inactive</p>
 <p className="text-2xl font-bold text-ink-secondary mt-1 tabular-nums">
 {statistics.inactive_spotlights || 0}
 </p>
 </div>
 <XCircle className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Status"
 value={filters.is_active}
 onChange={(value) => handleFilterChange('is_active', value)}
 options={activeStatusOptions}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading vendor spotlights…</p>
 </div>
 ) : (
 <DataTable
 data={spotlights}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search by name, category, description, or location..."
 onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
 emptyMessage="No vendor spotlights found"
 pageSize={10}
 showPagination={true}
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 {showCreateModal && (
 <CreateVendorSpotlightModal
 isOpen={showCreateModal}
 onClose={() => {
 setShowCreateModal(false);
 setEditingSpotlight(null);
 }}
 onSave={editingSpotlight ? handleUpdate : handleCreate}
 spotlight={editingSpotlight}
 isEdit={!!editingSpotlight}
 isLoading={isCreating || isUpdating}
 />
 )}
 </div>
 );
};

export default AdminVendorSpotlight;
